import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Company Name Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="px-8 py-6">
          <div className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/4757f670-e26b-4258-b6bb-7a38f4efc706.png" 
              alt="CASTALIA LTDA Logo" 
              className="h-10 w-10 object-contain"
            />
            <h1 className={`text-sm font-semibold tracking-[2px] transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white/90'
            }`}>
              CASTALIA LTDA
            </h1>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-r from-[#1a1a1a] to-[#0d1b2a]">
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-teal-500/10 rounded-full blur-2xl"></div>
        </div>

        <div className="container mx-auto px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="space-y-4">
                <h2 className="text-[120px] font-bold text-white leading-[0.95]">
                  Modern iOS
                </h2>
                <h2 className="text-[140px] italic font-serif text-transparent bg-clip-text bg-gradient-to-r from-white to-[#a8dadc] leading-[0.95]">
                  Applications
                </h2>
                <h2 className="text-[120px] font-bold text-white leading-[0.95]">
                  For Tomorrow
                </h2>
              </div>
              
              <div className="mt-20 max-w-[520px]">
                <p className="text-[22px] text-[#b8b8b8] font-light leading-relaxed">
                  Crafting premium VPN and Cleaner solutions
                </p>
                <p className="text-[22px] text-[#b8b8b8] font-light leading-relaxed">
                  Elevating mobile security and performance
                </p>
              </div>
            </div>

            {/* Right Visual Element */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-[400px] h-[280px] bg-[#0a1929] rounded-3xl shadow-2xl transform -rotate-3 p-8 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-[#ff6b35]/20 to-transparent rounded-3xl"></div>
                <img 
                  src="https://public.readdy.ai/ai/img_res/d710d38eea22e96ba803e9b8de6903b4.jpg" 
                  alt="iOS App Interface" 
                  className="w-full h-full object-contain relative z-10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#fafafa]">
        <div className="container mx-auto px-8 max-w-[1400px]">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Left Column */}
            <div className="lg:col-span-2">
              <p className="text-[11px] uppercase tracking-[3px] text-[#ff6b35] font-semibold mb-6">
                WHO WE ARE
              </p>
              <h3 className="text-[72px] font-bold text-[#1a1a1a] leading-tight">
                CASTALIA LTDA
              </h3>
            </div>

            {/* Right Column */}
            <div className="lg:col-span-3">
              <div className="bg-[#1a1a1a] rounded-[32px] p-20 relative">
                <div className="absolute top-8 right-8 w-3 h-3 bg-[#ff6b35] rounded-full"></div>
                
                <div className="space-y-8 text-white">
                  <p className="text-[18px] leading-[1.8] font-light">
                    CASTALIA LTDA is a team of experts dedicated to developing cutting-edge applications for the iOS platform. We combine technical excellence with innovative design to create solutions that truly make a difference in users' daily lives.
                  </p>
                  <p className="text-[18px] leading-[1.8] font-light">
                    Our flagship products, including advanced VPN solutions and intelligent Cleaner applications, are meticulously crafted to enhance user experience, strengthen mobile security, and optimize device performance. We believe in building software that is not only powerful but also intuitive and accessible.
                  </p>
                  <p className="text-[18px] leading-[1.8] font-light">
                    With a focus on privacy, performance, and user satisfaction, we continue to push the boundaries of what's possible on iOS, delivering applications that users can trust and rely on every day.
                  </p>
                </div>
              </div>
              
              <div className="mt-16">
                <div className="h-[2px] w-[30%] bg-gray-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="container mx-auto px-8">
          <div className="flex justify-between items-end mb-16">
            <div>
              <h3 className="text-[48px] font-serif text-gray-700 leading-tight">Our</h3>
              <h3 className="text-[64px] font-bold text-black leading-tight">Products</h3>
            </div>
            <div className="flex items-center gap-2 text-[16px] text-gray-600">
              <span>iOS Solutions</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </div>

          <div className="flex gap-10 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide">
            {/* VPN Product Card */}
            <div className="flex-shrink-0 w-[480px] h-[620px] bg-[#f8f6f3] rounded-[28px] overflow-hidden snap-start hover:-translate-y-2 transition-transform duration-300 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <div className="p-8">
                <span className="inline-block px-4 py-1 bg-[#1a1a1a] text-white text-sm rounded-full">
                  Security
                </span>
              </div>
              
              <div className="w-full h-[380px] px-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=premium%20VPN%20mobile%20application%20interface%20screenshot%20showing%20secure%20connection%20dashboard%20with%20shield%20icon%2C%20modern%20iOS%20design%2C%20clean%20minimalist%20UI%20with%20blue%20and%20white%20color%20scheme%2C%20connection%20status%20display%2C%20server%20selection%20menu%2C%20professional%20app%20mockup%20on%20simple%20light%20background&width=480&height=380&seq=vpn-product-card-001&orientation=portrait" 
                  alt="VPN Pro Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="p-10">
                <h4 className="text-[32px] font-bold text-black mb-4">VPN Pro</h4>
                <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                  Advanced VPN solution providing military-grade encryption, lightning-fast connections, and complete privacy protection for your iOS devices.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">256-bit Encryption</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">No Logs Policy</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Global Servers</span>
                </div>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="flex-shrink-0 w-[480px] h-[620px] bg-[#f8f6f3] rounded-[28px] overflow-hidden snap-start hover:-translate-y-2 transition-transform duration-300 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <div className="p-8">
                <span className="inline-block px-4 py-1 bg-[#1a1a1a] text-white text-sm rounded-full">
                  Optimization
                </span>
              </div>
              
              <div className="w-full h-[380px] px-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=mobile%20phone%20cleaner%20optimization%20app%20interface%20showing%20storage%20analysis%20with%20colorful%20charts%2C%20file%20management%20dashboard%2C%20iOS%20style%20design%2C%20clean%20modern%20UI%20with%20green%20and%20orange%20accents%2C%20performance%20metrics%20display%2C%20professional%20app%20screenshot%20on%20simple%20light%20background&width=480&height=380&seq=cleaner-product-card-002&orientation=portrait" 
                  alt="Cleaner Max Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="p-10">
                <h4 className="text-[32px] font-bold text-black mb-4">Cleaner Max</h4>
                <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                  Intelligent cleaning application that optimizes storage, removes junk files, and boosts your device performance with smart algorithms.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Smart Scan</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Safe Cleaning</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Speed Boost</span>
                </div>
              </div>
            </div>

            {/* Additional Product Card */}
            <div className="flex-shrink-0 w-[480px] h-[620px] bg-[#f8f6f3] rounded-[28px] overflow-hidden snap-start hover:-translate-y-2 transition-transform duration-300 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
              <div className="p-8">
                <span className="inline-block px-4 py-1 bg-[#1a1a1a] text-white text-sm rounded-full">
                  Privacy
                </span>
              </div>
              
              <div className="w-full h-[380px] px-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20privacy%20protection%20app%20interface%20showing%20security%20features%20dashboard%2C%20password%20manager%2C%20data%20encryption%20controls%2C%20modern%20minimalist%20design%20with%20purple%20and%20blue%20gradients%2C%20shield%20icons%2C%20professional%20mobile%20app%20mockup%20on%20simple%20light%20background&width=480&height=380&seq=privacy-product-card-003&orientation=portrait" 
                  alt="Privacy Suite Application" 
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="p-10">
                <h4 className="text-[32px] font-bold text-black mb-4">Privacy Suite</h4>
                <p className="text-[15px] text-gray-600 leading-relaxed mb-6">
                  Comprehensive privacy protection toolkit featuring secure browsing, data encryption, and advanced security features for complete peace of mind.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Data Protection</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Secure Vault</span>
                  <span className="px-3 py-1 border border-gray-300 rounded-full text-xs text-gray-700">Ad Blocking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-36 bg-gradient-to-b from-[#f5f5f5] to-white">
        <div className="container mx-auto px-8 max-w-[1400px]">
          <div className="text-center mb-20">
            <h3 className="text-[68px] font-bold text-black mb-4">
              <span className="text-gray-400">(</span>Key Features<span className="text-gray-400">)</span>
            </h3>
            <p className="text-[18px] text-gray-500">What makes us different</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-shield-check-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">Advanced Encryption</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Military-grade AES-256 encryption ensures your data remains completely secure and private, protecting you from any potential threats or unauthorized access.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">01</span>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-speed-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">Speed Optimization</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Lightning-fast performance with optimized servers worldwide. Experience seamless browsing and streaming without compromising on security or privacy.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">02</span>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-brush-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">Smart Cleaning</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Intelligent algorithms identify and remove unnecessary files, cache, and duplicates, freeing up valuable storage space while keeping important data safe.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">03</span>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-lock-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">Privacy Protection</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Comprehensive privacy features including no-logs policy, DNS leak protection, and automatic kill switch to ensure your online activities remain completely anonymous.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">04</span>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-smartphone-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">User-Friendly Interface</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Beautifully designed interfaces that are intuitive and easy to navigate. Connect with one tap and manage all features effortlessly on your iOS device.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">05</span>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-[24px] p-12 border border-[#e8e8e8] min-h-[340px] relative hover:shadow-lg transition-shadow duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c61] flex items-center justify-center">
                <i className="ri-customer-service-2-line text-white text-3xl"></i>
              </div>
              <h4 className="text-[24px] font-bold text-black mt-8 mb-5">24/7 Support</h4>
              <p className="text-[15px] text-[#666] leading-relaxed">
                Our dedicated support team is available around the clock to assist you with any questions or issues, ensuring you always have help when you need it.
              </p>
              <span className="absolute bottom-8 right-8 text-[48px] font-extralight text-[#f0f0f0]">06</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="relative py-24 bg-gradient-to-br from-[#0d3b3f] to-[#1a5f63] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.15]">
          <svg className="w-full h-full" viewBox="0 0 1200 600">
            <path d="M0 300 Q 300 200 600 300 T 1200 300" stroke="currentColor" fill="none" strokeWidth="1" className="text-teal-300"/>
            <path d="M0 350 Q 300 250 600 350 T 1200 350" stroke="currentColor" fill="none" strokeWidth="1" className="text-teal-300"/>
            <circle cx="200" cy="150" r="3" fill="currentColor" className="text-teal-300"/>
            <circle cx="500" cy="200" r="3" fill="currentColor" className="text-teal-300"/>
            <circle cx="800" cy="180" r="3" fill="currentColor" className="text-teal-300"/>
            <circle cx="1000" cy="220" r="3" fill="currentColor" className="text-teal-300"/>
          </svg>
        </div>

        <div className="container mx-auto px-8 relative z-10">
          {/* Upper Footer */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
            {/* Left Column */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-2 mb-12">
                <img 
                  src="https://public.readdy.ai/ai/img_res/4757f670-e26b-4258-b6bb-7a38f4efc706.png" 
                  alt="CASTALIA LTDA Logo" 
                  className="h-8 w-8 object-contain"
                />
                <h4 className="text-[28px] font-serif text-white">CASTALIA LTDA</h4>
                <div className="w-2 h-2 bg-[#ff6b35] rounded-full"></div>
              </div>
              
              <div className="mt-16">
                <p className="text-[32px] font-serif text-white leading-tight mb-2">Building the future</p>
                <p className="text-[32px] font-serif text-white leading-tight">of mobile security</p>
              </div>
            </div>

            {/* Right Navigation Grid */}
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-10">
              {/* Company Column */}
              <div>
                <h5 className="text-[16px] font-bold text-white uppercase mb-6">Company</h5>
                <ul className="space-y-4">
                  <li><a href="#about" className="text-white/80 hover:text-white transition-colors text-sm">About Us</a></li>
                  <li><a href="#products" className="text-white/80 hover:text-white transition-colors text-sm">Our Team</a></li>
                  <li><a href="#contact" className="text-white/80 hover:text-white transition-colors text-sm">Careers</a></li>
                </ul>
              </div>

              {/* Products Column */}
              <div>
                <h5 className="text-[16px] font-bold text-white uppercase mb-6">Products</h5>
                <ul className="space-y-4">
                  <li><a href="#products" className="text-white/80 hover:text-white transition-colors text-sm">VPN Solutions</a></li>
                  <li><a href="#products" className="text-white/80 hover:text-white transition-colors text-sm">Cleaner Apps</a></li>
                  <li><a href="#products" className="text-white/80 hover:text-white transition-colors text-sm">All Products</a></li>
                </ul>
              </div>

              {/* Resources Column */}
              <div>
                <h5 className="text-[16px] font-bold text-white uppercase mb-6">Resources</h5>
                <ul className="space-y-4">
                  <li><a href="#features" className="text-white/80 hover:text-white transition-colors text-sm">Documentation</a></li>
                  <li><a href="#contact" className="text-white/80 hover:text-white transition-colors text-sm">Support</a></li>
                  <li><a href="#features" className="text-white/80 hover:text-white transition-colors text-sm">FAQ</a></li>
                </ul>
              </div>

              {/* Contact Column */}
              <div>
                <h5 className="text-[16px] font-bold text-white uppercase mb-6">Contact</h5>
                <ul className="space-y-4 text-sm text-white/80">
                  <li className="flex items-start gap-2">
                    <i className="ri-map-pin-line mt-1 flex-shrink-0"></i>
                    <span>Address: CALLE 8 9 58</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-building-line mt-1 flex-shrink-0"></i>
                    <span>City: GARZON</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-mail-line mt-1 flex-shrink-0"></i>
                    <span>Zip: 414020</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Lower Footer Bar */}
          <div className="pt-12 border-t border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              {/* Left - Copyright */}
              <div>
                <p className="text-[13px] text-white/70">
                  © 2025 CASTALIA LTDA. All rights reserved.
                </p>
              </div>

              {/* Center - Social Icons */}
              <div className="flex justify-center gap-5">
                <a href="#" className="w-10 h-10 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-linkedin-line text-white text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-twitter-x-line text-white text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-facebook-line text-white text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 rounded-lg border border-white/40 flex items-center justify-center hover:bg-white/10 transition-colors">
                  <i className="ri-instagram-line text-white text-lg"></i>
                </a>
              </div>

              {/* Right - Contact Details */}
              <div className="text-right space-y-2">
                <p className="text-[13px] text-white/80 flex items-center justify-end gap-2">
                  <i className="ri-global-line"></i>
                  <span>Country: Colombia</span>
                </p>
                <p className="text-[13px] text-white/80 flex items-center justify-end gap-2">
                  <i className="ri-mail-line"></i>
                  <span>Email: help@castalialtda.com</span>
                </p>
                <p className="text-[13px] text-white/80 flex items-center justify-end gap-2">
                  <i className="ri-phone-line"></i>
                  <span>Phone: +1 3094603953</span>
                </p>
              </div>
            </div>

            {/* Quick Link */}
            <div className="mt-8 text-center">
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[11px] text-white/50 hover:text-white/80 transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
